import React, { useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  IconButton,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import LogoutIcon from '@mui/icons-material/LogoutOutlined';
import SwapIcon from '@mui/icons-material/SwapHorizOutlined';
import HomeWorkOutlinedIcon from '@mui/icons-material/HomeWorkOutlined';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import { useLanguage } from '../lib/i18n';

export type ConfirmDialogMode = 'logout' | 'change_property';

interface LogoutConfirmDialogProps {
  open: boolean;
  mode?: ConfirmDialogMode;
  onClose: () => void;
  onConfirm: () => Promise<void> | void;
  propertyNo?: string;
  ownerName?: string;
}

export function LogoutConfirmDialog({
  open,
  mode = 'logout',
  onClose,
  onConfirm,
  propertyNo,
  ownerName,
}: LogoutConfirmDialogProps) {
  const { t, lang } = useLanguage();
  const [loading, setLoading] = useState(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await onConfirm();
    } finally {
      setLoading(false);
      onClose();
    }
  };

  const isMr = lang === 'mr';
  const isChangeProperty = mode === 'change_property';

  // Dynamic styling and texts based on action mode
  const badgeBg = isChangeProperty ? '#EFF6FF' : '#FEE2E2';
  const badgeColor = isChangeProperty ? '#2563EB' : '#DC2626';
  const badgeShadow = isChangeProperty ? '0 0 0 8px #F0F7FF' : '0 0 0 8px #FEF2F2';

  const title = isChangeProperty
    ? (isMr ? 'मालमत्ता बदला' : 'Change Property')
    : (isMr ? 'बाहेर पडण्याची खात्री करा' : 'Confirm Logout');

  const description = isChangeProperty
    ? (isMr
        ? 'दुसरी मालमत्ता शोधण्यासाठी किंवा बदलण्यासाठी, तुम्हाला प्रथम या मालमत्ता सत्रातून बाहेर पडावे (Logout करावे) लागेल. तुम्ही पुढे जाऊ इच्छिता का?'
        : 'To switch or search for another property, you will need to log out of your current property session first. Do you want to proceed?')
    : (isMr
        ? 'तुम्हाला खात्री आहे की तुम्ही या मालमत्ता सत्रातून बाहेर पडू इच्छिता? तुमचे सक्रिय सत्र समाप्त होईल आणि मुख्य पृष्ठावर पुनर्निर्देशित केले जाईल.'
        : 'Are you sure you want to end your current session? You will be safely logged out and redirected to the home page.');

  const confirmBtnText = isChangeProperty
    ? (isMr ? 'मालमत्ता बदला' : 'Change Property')
    : (isMr ? 'लॉगआउट' : 'Logout');

  const confirmBtnBg = isChangeProperty ? '#2563EB' : '#DC2626';
  const confirmBtnHover = isChangeProperty ? '#1D4ED8' : '#B91C1C';
  const confirmBtnShadow = isChangeProperty
    ? '0 2px 8px rgba(37, 99, 235, 0.25)'
    : '0 2px 8px rgba(220, 38, 38, 0.25)';

  const actionIcon = isChangeProperty ? (
    <SwapIcon sx={{ fontSize: 16 }} />
  ) : (
    <LogoutIcon sx={{ fontSize: 16 }} />
  );

  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(6px)',
            backgroundColor: 'rgba(15, 23, 42, 0.45)',
          },
        },
        paper: {
          sx: {
            borderRadius: '20px',
            overflow: 'hidden',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            border: '1px solid #E2E8F0',
            p: { xs: 2.5, sm: 3 },
          },
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        disabled={loading}
        size="small"
        sx={{
          position: 'absolute',
          top: 14,
          right: 14,
          color: '#94A3B8',
          '&:hover': { color: '#475569', backgroundColor: '#F1F5F9' },
        }}
      >
        <CloseIcon fontSize="small" />
      </IconButton>

      {/* Top Icon Badge */}
      <Stack alignItems="center" spacing={2} sx={{ pt: 1, pb: 1 }}>
        <Box
          sx={{
            width: 60,
            height: 60,
            borderRadius: '50%',
            backgroundColor: badgeBg,
            color: badgeColor,
            display: 'grid',
            placeItems: 'center',
            boxShadow: badgeShadow,
          }}
        >
          {isChangeProperty ? (
            <SwapIcon sx={{ fontSize: 32 }} />
          ) : (
            <LogoutIcon sx={{ fontSize: 30 }} />
          )}
        </Box>

        <Box sx={{ textAlign: 'center' }}>
          <Typography variant="h6" sx={{ fontWeight: 800, color: '#0F172A', fontSize: '1.25rem' }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ color: '#64748B', mt: 0.75, lineHeight: 1.5, px: 1 }}>
            {description}
          </Typography>
        </Box>
      </Stack>

      {/* Active Property Card preview */}
      {(propertyNo || ownerName) && (
        <Paper
          elevation={0}
          sx={{
            my: 2,
            p: 1.75,
            borderRadius: '14px',
            backgroundColor: '#F8FAFC',
            border: '1px solid #E2E8F0',
          }}
        >
          <Stack spacing={0.75}>
            {propertyNo && (
              <Stack direction="row" spacing={1} alignItems="center">
                <HomeWorkOutlinedIcon sx={{ color: '#3B42C4', fontSize: 18 }} />
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                  {isMr ? 'मालमत्ता क्र.' : 'Current Property'}:
                </Typography>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0F172A' }}>
                  {propertyNo}
                </Typography>
              </Stack>
            )}
            {ownerName && (
              <Stack direction="row" spacing={1} alignItems="center">
                <PersonOutlineIcon sx={{ color: '#64748B', fontSize: 18 }} />
                <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 600 }}>
                  {isMr ? 'मालक' : 'Owner'}:
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155' }} noWrap>
                  {ownerName}
                </Typography>
              </Stack>
            )}
          </Stack>
        </Paper>
      )}

      {/* Actions */}
      <DialogActions
        sx={{
          p: 0,
          pt: 2,
          display: 'flex',
          gap: 1.25,
          '& > button': {
            flex: 1,
            m: '0 !important',
          },
        }}
      >
        <Button
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            height: { xs: 38, sm: 40 },
            borderRadius: '10px',
            borderColor: '#CBD5E1',
            color: '#475569',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            whiteSpace: 'nowrap',
            px: { xs: 1.25, sm: 2 },
            '&:hover': {
              borderColor: '#94A3B8',
              backgroundColor: '#F8FAFC',
            },
          }}
        >
          {t('cancel') || (isMr ? 'रद्द करा' : 'Cancel')}
        </Button>

        <Button
          onClick={handleConfirm}
          disabled={loading}
          variant="contained"
          sx={{
            height: { xs: 38, sm: 40 },
            borderRadius: '10px',
            backgroundColor: confirmBtnBg,
            color: '#FFFFFF',
            fontWeight: 600,
            textTransform: 'none',
            fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            whiteSpace: 'nowrap',
            px: { xs: 1.25, sm: 2 },
            boxShadow: confirmBtnShadow,
            '&:hover': {
              backgroundColor: confirmBtnHover,
            },
          }}
          startIcon={loading ? undefined : actionIcon}
        >
          {loading ? (
            <CircularProgress size={18} color="inherit" />
          ) : (
            confirmBtnText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
